import { z } from 'zod';
import type { EnquirerType, EnquiryProjectKind } from 'generated/graphql';
import {
    ENQUIRER_TYPE_OPTIONS,
    PROJECT_KIND_OPTIONS,
} from '~/modules/business-manager/forms/types';
import type {
    EnquiryFormSubmissionContext,
    PublicEnquiryForm,
    PublicEnquiryFormField,
    PublicEnquiryFormType,
} from '../types/public-enquiry-form';
import type { EnquiryFormSelection } from '../hooks/use-enquiry';
import { countryCodeForName } from '../util/iso-countries';
import {
    predictEnquiryDestination,
    type PredictedDestination,
} from '../util/predict-enquiry-destination';
import {
    compactFieldAnswers,
    validateFieldAnswers,
} from '../util/custom-field-validation';

/** The location question's label when the form does not rename it. */
export const DEFAULT_LOCATION_LABEL = 'Suburb / Postcode';

/** What each fixed qualifying question says when it is left unanswered. */
export const CONFIGURED_ENQUIRY_ERRORS = {
    enquiryType: 'Please choose what your enquiry is about.',
    enquirerType: 'Please tell us who you are.',
    projectKind: 'Please choose residential or commercial.',
} as const;

/**
 * Which parts of the enquiry form a configured Business Manager form switches
 * on, and how each behaves. With no form every value is the flat-settings
 * default, so a host renders exactly what it did before Forms existed.
 */
export interface ConfiguredEnquiryRules {
    /** "What is your enquiry about?" options; empty when the question is off. */
    enquiryTypes: PublicEnquiryFormType[];
    /** "I am a…" */
    asksEnquirerType: boolean;
    /** "Is this residential or commercial?" */
    asksProjectKind: boolean;
    collectLocation: boolean;
    locationLabel: string;
    messageEnabled: boolean;
    /** Only ever true while `messageEnabled` is. */
    messageRequired: boolean;
    attachmentsEnabled: boolean;
    /** Owner-defined questions, answered by field key. */
    customFields: PublicEnquiryFormField[];
    requiredCustomFields: PublicEnquiryFormField[];
    /** Shown under the submit button; empty for none. */
    consentText: string;
    /** The form's own submit wording, or null for the host's default. */
    submitLabel: string | null;
    /** The form's thank-you copy, or null for the host's default. */
    successMessage: string | null;
}

export interface ConfiguredEnquiryRuleOptions {
    /**
     * The host allows a location question at all. Defaults to `true`; a host
     * that never collects the reader's location passes `false` and the form
     * cannot switch it back on.
     */
    allowLocation?: boolean;
    /** Ask for a location when no form is configured. Defaults to `true`. */
    locationWithoutForm?: boolean;
}

const nonEmpty = (value: string | null | undefined): string | null =>
    value?.trim() || null;

/** Field visibility and required rules for a form, or the flat defaults without one. */
export function getConfiguredEnquiryRules(
    form: PublicEnquiryForm | null,
    {
        allowLocation = true,
        locationWithoutForm = true,
    }: ConfiguredEnquiryRuleOptions = {},
): ConfiguredEnquiryRules {
    // Gated download forms drop the message box: the visitor only leaves details.
    const messageEnabled = form?.messageEnabled ?? true;
    const customFields = form?.fields ?? [];
    const isWebsite = form?.kind === 'Website';
    return {
        enquiryTypes: form?.enquiryTypesEnabled ? form.enquiryTypes : [],
        asksEnquirerType: !!form?.enquirerTypeEnabled,
        asksProjectKind: !!form?.projectKindQuestionEnabled,
        collectLocation:
            allowLocation &&
            (form ? form.locationEnabled : locationWithoutForm),
        // Website forms can rename the location question; platform forms keep the stock wording.
        locationLabel:
            (isWebsite && nonEmpty(form.locationLabel)) ||
            DEFAULT_LOCATION_LABEL,
        messageEnabled,
        // Website forms can leave the message optional; on the platform it is the enquiry.
        messageRequired: messageEnabled && (form?.messageRequired ?? true),
        // The server derives this for platform forms (attachments follow the
        // message box) and lets Website forms decide independently.
        attachmentsEnabled: form?.attachmentsEnabled ?? messageEnabled,
        customFields,
        requiredCustomFields: customFields.filter((field) => field.required),
        consentText: isWebsite ? form.consentText.trim() : '',
        submitLabel: nonEmpty(form?.submitLabel),
        successMessage: nonEmpty(form?.successMessage),
    };
}

/** The visitor's answers to a form's qualifying and custom questions. */
export interface ConfiguredEnquiryAnswers {
    /** The chosen enquiry type id, as the select's string value. */
    typeId: string;
    enquirerType: EnquirerType | '';
    projectKind: EnquiryProjectKind | '';
    /** Custom question answers by field key. */
    fieldAnswers: Record<string, string>;
}

export const EMPTY_CONFIGURED_ANSWERS: ConfiguredEnquiryAnswers = {
    typeId: '',
    enquirerType: '',
    projectKind: '',
    fieldAnswers: {},
};

/** A message per unanswered or invalid question; null / absent when it passes. */
export interface ConfiguredEnquiryErrors {
    enquiryType: string | null;
    enquirerType: string | null;
    projectKind: string | null;
    /** Custom question errors by field key. */
    fields: Record<string, string>;
}

export const NO_CONFIGURED_ERRORS: ConfiguredEnquiryErrors = {
    enquiryType: null,
    enquirerType: null,
    projectKind: null,
    fields: {},
};

const enumOrBlank = (
    asked: boolean,
    values: readonly { value: string }[],
    message: string,
) =>
    asked
        ? z
              .string()
              .refine(
                  (value) => values.some((option) => option.value === value),
                  { message },
              )
        : z.string();

/**
 * The Zod schema for a form's own questions. It sits beside the contact
 * details schema (`getEnquiryFormSchema`) rather than inside it because these
 * answers live outside react-hook-form: custom questions change shape per form.
 */
export function getConfiguredAnswersSchema(rules: ConfiguredEnquiryRules) {
    return z.object({
        typeId:
            rules.enquiryTypes.length > 0
                ? z
                      .string()
                      .refine(
                          (id) =>
                              rules.enquiryTypes.some(
                                  (type) => String(type.id) === id,
                              ),
                          { message: CONFIGURED_ENQUIRY_ERRORS.enquiryType },
                      )
                : z.string(),
        enquirerType: enumOrBlank(
            rules.asksEnquirerType,
            ENQUIRER_TYPE_OPTIONS,
            CONFIGURED_ENQUIRY_ERRORS.enquirerType,
        ),
        projectKind: enumOrBlank(
            rules.asksProjectKind,
            PROJECT_KIND_OPTIONS,
            CONFIGURED_ENQUIRY_ERRORS.projectKind,
        ),
        fieldAnswers: z.record(z.string()).superRefine((answers, ctx) => {
            const errors = validateFieldAnswers(rules.customFields, answers);
            for (const [key, message] of Object.entries(errors)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [key],
                    message,
                });
            }
        }),
    });
}

/** Every problem with the answers at once, so fixing one never just reveals the next. */
export function validateConfiguredAnswers(
    rules: ConfiguredEnquiryRules,
    answers: ConfiguredEnquiryAnswers,
): ConfiguredEnquiryErrors {
    const result = getConfiguredAnswersSchema(rules).safeParse(answers);
    if (result.success) return NO_CONFIGURED_ERRORS;
    const errors: ConfiguredEnquiryErrors = {
        ...NO_CONFIGURED_ERRORS,
        fields: {},
    };
    for (const issue of result.error.issues) {
        const [question, fieldKey] = issue.path;
        if (question === 'fieldAnswers' && typeof fieldKey === 'string') {
            errors.fields[fieldKey] = issue.message;
        } else if (question === 'typeId') {
            errors.enquiryType = issue.message;
        } else if (question === 'enquirerType') {
            errors.enquirerType = issue.message;
        } else if (question === 'projectKind') {
            errors.projectKind = issue.message;
        }
    }
    return errors;
}

export const hasConfiguredErrors = (errors: ConfiguredEnquiryErrors): boolean =>
    !!errors.enquiryType ||
    !!errors.enquirerType ||
    !!errors.projectKind ||
    Object.keys(errors.fields).length > 0;

export const findSelectedType = (
    rules: ConfiguredEnquiryRules,
    typeId: string,
): PublicEnquiryFormType | undefined =>
    rules.enquiryTypes.find((type) => String(type.id) === typeId);

/**
 * Mirrors the server: a country the visitor picked in a Country question beats
 * the one detected from their connection when rules check location.
 */
export function getRoutingCountry(
    customFields: PublicEnquiryFormField[],
    fieldAnswers: Record<string, string>,
    visitorCountry: string | null | undefined,
): string | null {
    for (const field of customFields) {
        if (field.type !== 'Country') continue;
        const code = countryCodeForName((fieldAnswers[field.key] ?? '').trim());
        if (code) return code;
    }
    return visitorCountry ?? null;
}

/** Where the answers route, as far as the client can tell. Null without a form. */
export function predictConfiguredDestination(
    form: PublicEnquiryForm | null,
    rules: ConfiguredEnquiryRules,
    answers: ConfiguredEnquiryAnswers,
    location: {
        country: string | null;
        regionName: string | null;
        /** The branch picked on a form that asks which branch. */
        branchId?: number | null;
    },
): PredictedDestination | null {
    if (!form) return null;
    return predictEnquiryDestination(form, {
        branchId: location.branchId ?? null,
        typeId: findSelectedType(rules, answers.typeId)?.id ?? null,
        enquirerType: answers.enquirerType || null,
        projectKind: answers.projectKind || null,
        country: location.country,
        regionName: location.regionName,
        fieldAnswers: compactFieldAnswers(
            rules.customFields,
            answers.fieldAnswers,
        ),
    });
}

/** The per-submission choices `submitEnquiry` sends alongside the contact details. */
export function buildConfiguredSelection(
    rules: ConfiguredEnquiryRules,
    answers: ConfiguredEnquiryAnswers,
    predicted: PredictedDestination | null,
): EnquiryFormSelection {
    return {
        enquiryFormTypeId: findSelectedType(rules, answers.typeId)?.id,
        enquirerType: answers.enquirerType || undefined,
        projectKind: answers.projectKind || undefined,
        requestBooking: predicted?.destination === 'Calendar',
        fieldAnswers:
            rules.customFields.length > 0
                ? compactFieldAnswers(rules.customFields, answers.fieldAnswers)
                : undefined,
    };
}

/** The professional fields a configured form's message settings replace. */
export interface EnquiryMessageSettings {
    CustomEnquiryMessage?: string;
    EnquiryMessageOverrideEnabled?: boolean;
    EnquiryMessagePlaceholder?: string;
}

/**
 * A configured form owns the message settings the flat professional fields
 * carry, so it overrides them. Returns the professional untouched without one.
 */
export function applyFormMessageSettings<P extends EnquiryMessageSettings>(
    professional: P,
    form: PublicEnquiryForm | null,
): P {
    if (!form) return professional;
    return {
        ...professional,
        CustomEnquiryMessage: form.customMessage ?? undefined,
        EnquiryMessageOverrideEnabled: form.messageOverrideEnabled,
        EnquiryMessagePlaceholder: form.messagePlaceholder ?? undefined,
    };
}

export interface FormContextOptions {
    /** Where the submission comes from. Defaults to `archipro`. */
    channel?: EnquiryFormSubmissionContext['channel'];
    /** Host page details for embedded forms, stamped on the enquiry. */
    submissionSource?: Pick<
        EnquiryFormSubmissionContext,
        'sourceUrl' | 'sourceParams' | 'captchaToken'
    >;
    declineHandling?: EnquiryFormSubmissionContext['declineHandling'];
}

/** What every submission of a configured form carries. Undefined without a form. */
export function buildFormContext(
    form: PublicEnquiryForm | null,
    { channel, submissionSource, declineHandling }: FormContextOptions = {},
): EnquiryFormSubmissionContext | undefined {
    if (!form) return undefined;
    return {
        enquiryFormId: form.id,
        channel: channel ?? 'archipro',
        sourceUrl: submissionSource?.sourceUrl,
        sourceParams: submissionSource?.sourceParams,
        captchaToken: submissionSource?.captchaToken,
        ...(declineHandling && { declineHandling }),
    };
}

const stringOrUndefined = (
    value: string | number | undefined,
): string | undefined => (value === undefined ? undefined : String(value));

const jsonOrUndefined = (
    value: Record<string, string> | undefined,
): string | undefined => (value ? JSON.stringify(value) : undefined);

/**
 * The Forms fields of the submit route's form data: which form and option the
 * visitor used, their qualifying answers, custom answers as JSON, and where an
 * embedded form was hosted. Values the submission does not have are
 * `undefined`, so they are left out of the post.
 */
export function buildEnquiryFormPayload(
    formContext: EnquiryFormSubmissionContext | undefined,
    selection: EnquiryFormSelection | undefined,
): Record<string, string | undefined> {
    return {
        enquiryFormId: stringOrUndefined(formContext?.enquiryFormId),
        enquiryFormTypeId: stringOrUndefined(
            selection?.enquiryFormTypeId ?? formContext?.enquiryFormTypeId,
        ),
        enquirerType: selection?.enquirerType ?? formContext?.enquirerType,
        projectKind: selection?.projectKind ?? formContext?.projectKind,
        requestBooking:
            formContext &&
            (selection?.requestBooking ?? formContext.requestBooking)
                ? 'true'
                : undefined,
        channel: formContext?.channel,
        fieldAnswers: jsonOrUndefined(
            selection?.fieldAnswers ?? formContext?.fieldAnswers,
        ),
        sourceUrl: formContext?.sourceUrl,
        sourceParams: jsonOrUndefined(formContext?.sourceParams),
        captchaToken: formContext?.captchaToken,
        declineHandling: formContext?.declineHandling,
    };
}

/**
 * The payload fields worth resending after a sign-in interrupts the submit.
 * The captcha token is single use, so it never survives the round-trip.
 */
export function persistableFormPayload(
    payload: Record<string, string | undefined>,
): Record<string, string> | undefined {
    const kept: Record<string, string> = {};
    for (const [key, value] of Object.entries(payload)) {
        if (key === 'captchaToken' || value === undefined) continue;
        kept[key] = value;
    }
    return Object.keys(kept).length > 0 ? kept : undefined;
}
